/**
 * Node Factories - Functions for creating database explorer tree nodes
 */

import { DatabaseObject } from '../../database-objects/models/databaseObjectTypes';
import { createPinnedStorageNode, storageNodeId } from './azureStorageNodeFactories';
import {
  createConnectionNodeId,
  createDatabaseNodeId,
  createDatabasesFolderNodeId,
  createTablesFolderNodeId,
  createViewsFolderNodeId,
  createProgrammabilityFolderNodeId,
  createStoredProceduresFolderNodeId,
  createFunctionsFolderNodeId,
  createDatabaseObjectNodeId,
  createSystemDatabasesFolderNodeId,
  createSchemaContainerFolderNodeId,
  createPackagesFolderNodeId,
  createSequencesFolderNodeId,
  createSynonymsFolderNodeId,
  createDbLinksFolderNodeId,
  createMaterializedViewsFolderNodeId,
  createTypesFolderNodeId,
  createDirectoriesFolderNodeId,
  createExternalTablesFolderNodeId,
  createPropertyGraphsFolderNodeId,
  createUserTypesFolderNodeId,
  createTableTypesFolderNodeId,
  createAliasTypesFolderNodeId,
  createClrTypesFolderNodeId,
  createNodeId,
} from '../utils/nodeIdUtils';
import {
  ConnectionTreeNode,
  ConnectionNodeMetadata,
  DatabaseTreeNode,
  DatabaseNodeMetadata,
  DatabasesFolderNode,
  SystemDatabasesFolderNode,
  SchemaContainerFolderNode,
  TablesFolderNode,
  ViewsFolderNode,
  ProgrammabilityFolderNode,
  StoredProceduresFolderNode,
  FunctionsFolderNode,
  PackagesFolderNode,
  SequencesFolderNode,
  SynonymsFolderNode,
  DbLinksFolderNode,
  MaterializedViewsFolderNode,
  TypesFolderNode,
  DirectoriesFolderNode,
  ExternalTablesFolderNode,
  PropertyGraphsFolderNode,
  UserTypesFolderNode,
  TableTypesFolderNode,
  AliasTypesFolderNode,
  ClrTypesFolderNode,
  FolderTreeNode,
  DatabaseObjectTreeNode,
  PinnedDatabaseNode,
  PinnedSchemaNode,
  PinnedFolderNode,
  PinnedObjectNode,
  PinnedWorkspaceNode,
  FolderNodeBaseMetadata,
  SchemaContainerFolderMetadata,
} from './nodeTypes';
import type { PinnedObjectRef, PinnedWorkspaceRef } from '../../../types/electron';
import { DatabaseEngineId } from '../../../core/engines/engineTypes';
import {
  createMssqlDatabaseSecurityFolders,
  createPgsqlDatabaseSecurityFolders,
} from './securityNodeFactories';

/**
 * Checks if a database is a system database
 * Includes both MSSQL and PostgreSQL system databases
 */
export function isSystemDatabase(databaseName: string): boolean {
  const systemDatabases = [
    // MSSQL system databases
    'master', 'model', 'msdb', 'tempdb',
    // PostgreSQL system databases
    'postgres', 'template0', 'template1',
  ];
  return systemDatabases.includes(databaseName.toLowerCase());
}

/**
 * Creates a connection tree node
 */
export function createConnectionNode(
  connectionId: string,
  name: string,
  metadata: Omit<ConnectionNodeMetadata, 'id'>
): ConnectionTreeNode {
  return {
    id: createConnectionNodeId(connectionId),
    parentId: null,
    name,
    type: 'connection',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      ...metadata,
      id: connectionId
    }
  };
}

/**
 * Creates a database tree node
 */
export function createDatabaseNode(
  connectionId: string,
  databaseName: string,
  metadata: Partial<Omit<DatabaseNodeMetadata, 'connectionId' | 'name'>> = {}
): DatabaseTreeNode {
  // Determine if this is a system database
  const isSystem = isSystemDatabase(databaseName);

  // Set the correct parent ID based on database type
  const parentId = isSystem
    ? createSystemDatabasesFolderNodeId(connectionId) // System database goes under System Databases folder
    : createDatabasesFolderNodeId(connectionId);      // User database goes directly under Databases folder

  return {
    id: createDatabaseNodeId(connectionId, databaseName),
    parentId: parentId,
    name: databaseName,
    type: 'database',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      name: databaseName,
      ...metadata
    }
  };
}

/**
 * Creates a databases folder node under a connection
 */
export function createDatabasesFolderNode(
  connectionId: string,
  engine?: DatabaseEngineId
): DatabasesFolderNode {
  return {
    id: createDatabasesFolderNodeId(connectionId),
    parentId: createConnectionNodeId(connectionId),
    name: engine === 'oracle' ? 'Schemas' : 'Databases',
    type: 'databases-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: true, // Always expanded by default
    metadata: {
      connectionId,
      ...(engine ? { engine } : {}),
    }
  };
}

/**
 * Creates a system databases folder node
 */
export function createSystemDatabasesFolderNode(connectionId: string): SystemDatabasesFolderNode {
  return {
    id: createSystemDatabasesFolderNodeId(connectionId),
    parentId: createDatabasesFolderNodeId(connectionId),
    name: 'System Databases',
    type: 'system-databases-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId
    }
  };
}

/**
 * Creates a tables folder node under a database
 */
export function createTablesFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): TablesFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createTablesFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createDatabaseNodeId(connectionId, database),
    name: 'Tables',
    type: 'tables-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      ...(schema ? { schema } : {}),
    }
  };
}

/**
 * Creates a views folder node under a database
 */
export function createViewsFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): ViewsFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createViewsFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createDatabaseNodeId(connectionId, database),
    name: 'Views',
    type: 'views-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      ...(schema ? { schema } : {}),
    }
  };
}

/**
 * Creates a programmability folder node under a database
 */
export function createProgrammabilityFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): ProgrammabilityFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createProgrammabilityFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createDatabaseNodeId(connectionId, database),
    name: 'Programmability',
    type: 'programmability-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      ...(schema ? { schema } : {}),
    }
  };
}

/**
 * Creates a stored procedures folder node under programmability
 */
export function createStoredProceduresFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): StoredProceduresFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createStoredProceduresFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createProgrammabilityFolderNodeId(connectionId, database, schema),
    name: 'Stored Procedures',
    type: 'stored-procedures-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      ...(schema ? { schema } : {}),
    }
  };
}

/**
 * Creates a functions folder node under programmability
 */
export function createFunctionsFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): FunctionsFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createFunctionsFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createProgrammabilityFolderNodeId(connectionId, database, schema),
    name: 'Functions',
    type: 'functions-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      ...(schema ? { schema } : {}),
    }
  };
}

// Oracle-specific folder factories
export function createPackagesFolderNode(connectionId: string, database: string): PackagesFolderNode {
  return {
    id: createPackagesFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Packages',
    type: 'packages-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

export function createSequencesFolderNode(connectionId: string, database: string): SequencesFolderNode {
  return {
    id: createSequencesFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Sequences',
    type: 'sequences-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

export function createSynonymsFolderNode(connectionId: string, database: string): SynonymsFolderNode {
  return {
    id: createSynonymsFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Synonyms',
    type: 'synonyms-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

export function createDbLinksFolderNode(connectionId: string, database: string): DbLinksFolderNode {
  return {
    id: createDbLinksFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'DB Links',
    type: 'db-links-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

export function createMaterializedViewsFolderNode(connectionId: string, database: string): MaterializedViewsFolderNode {
  return {
    id: createMaterializedViewsFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Materialized Views',
    type: 'materialized-views-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

export function createTypesFolderNode(connectionId: string, database: string): TypesFolderNode {
  return {
    id: createTypesFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Types',
    type: 'types-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

export function createDirectoriesFolderNode(connectionId: string, database: string): DirectoriesFolderNode {
  return {
    id: createDirectoriesFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Directories',
    type: 'directories-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

// Kusto-specific folder factories
export function createExternalTablesFolderNode(connectionId: string, database: string): ExternalTablesFolderNode {
  return {
    id: createExternalTablesFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'External Tables',
    type: 'external-tables-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

// Property-graph folder factory (Oracle 23ai / PostgreSQL 19)
export function createPropertyGraphsFolderNode(connectionId: string, database: string): PropertyGraphsFolderNode {
  return {
    id: createPropertyGraphsFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Property Graphs',
    type: 'property-graphs-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database },
  };
}

// ---------------------------------------------------------------------------
// SQL Server user-defined types (Programmability -> Types)
// ---------------------------------------------------------------------------

/**
 * `Types` container under Programmability. Its three children are built here
 * so the node never needs a listing fetch of its own — see
 * `objectFolderUtils.objectFolderTypeMap` for why that matters.
 */
export function createUserTypesFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): UserTypesFolderNode {
  const { schema, parentId } = opts;
  const id = createUserTypesFolderNodeId(connectionId, database, schema);
  return {
    id,
    parentId: parentId ?? createProgrammabilityFolderNodeId(connectionId, database, schema),
    name: 'Types',
    type: 'user-types-folder',
    canHaveChildren: true,
    children: [
      createTableTypesFolderNode(connectionId, database, { schema, parentId: id }),
      createAliasTypesFolderNode(connectionId, database, { schema, parentId: id }),
      createClrTypesFolderNode(connectionId, database, { schema, parentId: id }),
    ],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      ...(schema ? { schema } : {}),
    },
  };
}

export function createTableTypesFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): TableTypesFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createTableTypesFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createUserTypesFolderNodeId(connectionId, database, schema),
    name: 'User-Defined Table Types',
    type: 'table-types-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database, ...(schema ? { schema } : {}) },
  };
}

export function createAliasTypesFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): AliasTypesFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createAliasTypesFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createUserTypesFolderNodeId(connectionId, database, schema),
    name: 'User-Defined Data Types',
    type: 'alias-types-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database, ...(schema ? { schema } : {}) },
  };
}

export function createClrTypesFolderNode(
  connectionId: string,
  database: string,
  opts: { schema?: string; parentId?: string } = {},
): ClrTypesFolderNode {
  const { schema, parentId } = opts;
  return {
    id: createClrTypesFolderNodeId(connectionId, database, schema),
    parentId: parentId ?? createUserTypesFolderNodeId(connectionId, database, schema),
    name: 'User-Defined Types (CLR)',
    type: 'clr-types-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId, database, ...(schema ? { schema } : {}) },
  };
}

/**
 * Creates a schema-container folder node placed directly under a database when
 * schema grouping is active.
 */
export function createSchemaContainerFolderNode(args: {
  connectionId: string;
  database: string;
  schema: string;
  objectCount: number;
  engine?: DatabaseEngineId;
}): SchemaContainerFolderNode {
  const { connectionId, database, schema, objectCount, engine } = args;
  return {
    id: createSchemaContainerFolderNodeId(connectionId, database, schema),
    parentId: createDatabaseNodeId(connectionId, database),
    name: schema,
    type: 'schema-container-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      objectCount,
      ...(engine ? { engine } : {}),
    },
  };
}

/**
 * Creates all the standard database object type folders under a database
 * Includes engine-specific security folders when engine is provided
 *
 * `isKustoProxy` marks an Azure Monitor ADX proxy connection (App Insights /
 * Log Analytics). Those endpoints have no materialized-view or external-table
 * concept at all — `.show materialized-views` / `.show external tables` aren't
 * in their management-command grammar and return HTTP 400 — so the folders are
 * omitted rather than rendered permanently empty or erroring. Resolve it with
 * `isKustoProxyUrl(connection.server)`.
 */
export function createDatabaseObjectTypeFolders(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId,
  isKustoProxy = false
): FolderTreeNode[] {
  // Kusto has no views/stored-procedures/programmability/security concept —
  // it gets its own fixed folder set instead of falling through to the
  // SQL-engine default below. Functions is a top-level folder (parented
  // directly under the database), not nested under Programmability.
  if (engine === 'kusto') {
    return [
      createTablesFolderNode(connectionId, database),
      ...(isKustoProxy ? [] : [createMaterializedViewsFolderNode(connectionId, database)]),
      createFunctionsFolderNode(connectionId, database, {
        parentId: createDatabaseNodeId(connectionId, database),
      }),
      ...(isKustoProxy ? [] : [createExternalTablesFolderNode(connectionId, database)]),
    ];
  }

  // Create the basic object type folders
  const tablesFolder = createTablesFolderNode(connectionId, database);
  const viewsFolder = createViewsFolderNode(connectionId, database);

  // Create the programmability folder
  const programmabilityFolder = createProgrammabilityFolderNode(connectionId, database);

  // Add stored procedures and functions under programmability
  const spFolder = createStoredProceduresFolderNode(connectionId, database);
  const fnFolder = createFunctionsFolderNode(connectionId, database);

  const programmabilityChildren: FolderTreeNode[] = [spFolder, fnFolder];

  // SQL Server only: Programmability -> Types -> the three user-defined-type
  // folders, matching SSMS / Azure Data Studio. The mock engine is deliberately
  // excluded — it cannot answer `sys.table_types`, so a mock tree would grow
  // three permanently-empty folders.
  if (engine === 'mssql') {
    programmabilityChildren.push(createUserTypesFolderNode(connectionId, database));
  }

  programmabilityFolder.children = programmabilityChildren;

  const folders: FolderTreeNode[] = [tablesFolder, viewsFolder, programmabilityFolder];

  // Add Oracle-specific top-level folders
  if (engine === 'oracle') {
    folders.push(
      createPackagesFolderNode(connectionId, database),
      createSequencesFolderNode(connectionId, database),
      createSynonymsFolderNode(connectionId, database),
      createDbLinksFolderNode(connectionId, database),
      createMaterializedViewsFolderNode(connectionId, database),
      createTypesFolderNode(connectionId, database),
      createDirectoriesFolderNode(connectionId, database),
      createPropertyGraphsFolderNode(connectionId, database),
    );
  }

  // Add engine-specific security folders
  if (engine === 'mssql' || engine === 'mock') {
    // Mock engine uses MSSQL-style security folders for testing
    folders.push(...createMssqlDatabaseSecurityFolders(connectionId, database));
  } else if (engine === 'pgsql') {
    folders.push(createPropertyGraphsFolderNode(connectionId, database));
    folders.push(...createPgsqlDatabaseSecurityFolders(connectionId, database));
  }

  return folders;
}

/**
 * Creates a database object tree node
 */
export function createDatabaseObjectNode(
  databaseObject: DatabaseObject,
  parentNodeId: string
): DatabaseObjectTreeNode {
  // Get database from metadata to ensure unique node IDs across databases
  const database = (databaseObject.metadata?.database as string) || '';

  // Create a node ID using the 5-parameter version which includes database
  // Format: object<->connectionId<->database<->type<->schema<->objectName
  const objId = createDatabaseObjectNodeId(
    databaseObject.connectionId,
    database,
    databaseObject.type,
    databaseObject.schema,
    databaseObject.name
  );

  return {
    id: objId,
    parentId: parentNodeId,
    name: databaseObject.name,
    type: databaseObject.type,
    canHaveChildren: ['table', 'view', 'package'].includes(databaseObject.type),
    children: [],
    isExpanded: false,
    metadata: {
      objectDetails: databaseObject
    }
  };
}

// ============================================================================
// Pinned Object Explorer Node Factories
// ============================================================================

/**
 * Mapping from the on-disk ref's camelCase `folder` enum to the kebab-case
 * folder-node type literal used everywhere else in the explorer (icon map,
 * refresh routing, context-menu generators, ...). Keeping the enum in the
 * persisted ref preserves the original on-disk shape; the renderer-side
 * folder kind is always the kebab form.
 */
const FOLDER_REF_TO_NODE_TYPE: Record<
  Extract<PinnedObjectRef, { kind: 'folder' }>['folder'],
  | 'tables-folder' | 'views-folder' | 'stored-procedures-folder' | 'functions-folder' | 'programmability-folder'
  // Oracle-specific folder node types.
  | 'packages-folder' | 'sequences-folder' | 'synonyms-folder' | 'db-links-folder'
  | 'materialized-views-folder' | 'types-folder' | 'directories-folder'
  // SQL Server user-defined-type folder node types.
  | 'table-types-folder' | 'alias-types-folder' | 'clr-types-folder'
> = {
  tables: 'tables-folder',
  views: 'views-folder',
  storedProcedures: 'stored-procedures-folder',
  functions: 'functions-folder',
  programmability: 'programmability-folder',
  packages: 'packages-folder',
  sequences: 'sequences-folder',
  synonyms: 'synonyms-folder',
  dbLinks: 'db-links-folder',
  materializedViews: 'materialized-views-folder',
  types: 'types-folder',
  directories: 'directories-folder',
  tableTypes: 'table-types-folder',
  aliasTypes: 'alias-types-folder',
  clrTypes: 'clr-types-folder',
};

/**
 * Mapping from the on-disk ref's camelCase `objectType` enum to the kebab-case
 * `DatabaseObjectType` literal used by context menus, refresh routing, the
 * icon map, and TanStack Query keys. Without this translation, downstream
 * switches on `node.objectType === 'stored-procedure'` silently missed
 * pinned-object rows (whose objectType was 'storedProcedure').
 */
const OBJECT_REF_TO_OBJECT_TYPE: Record<
  Extract<PinnedObjectRef, { kind: 'object' }>['objectType'],
  | 'table' | 'view' | 'stored-procedure' | 'function'
  // Oracle-specific kebab-case `DatabaseObjectType` literals.
  | 'package' | 'sequence' | 'synonym' | 'db-link' | 'materialized-view' | 'type' | 'directory'
  // SQL Server user-defined-type object literals.
  | 'table-type' | 'alias-type' | 'clr-type'
> = {
  table: 'table',
  view: 'view',
  storedProcedure: 'stored-procedure',
  function: 'function',
  package: 'package',
  sequence: 'sequence',
  synonym: 'synonym',
  dbLink: 'db-link',
  materializedView: 'materialized-view',
  type: 'type',
  directory: 'directory',
  tableType: 'table-type',
  aliasType: 'alias-type',
  clrType: 'clr-type',
};

/**
 * Stable id for a pinned wrapper node. Uses the canonical `<->` separator
 * (`NODE_ID_SEPARATOR`) so the id parses through the same helpers as every
 * other tree-node id (`getConnectionIdFromNodeId`, `getDatabaseFromNodeId`,
 * `parseNodeId`). The previous `pinned:cid:db:NAME` format used ':' / '/'
 * which collided for identifiers containing those characters AND was opaque
 * to the selection-context helpers.
 */
export function pinnedNodeId(connectionId: string, ref: PinnedObjectRef): string {
  if (ref.kind === 'database') {
    return createNodeId('pinned-database', connectionId, ref.database);
  }
  if (ref.kind === 'schema') {
    return createNodeId('pinned-schema', connectionId, ref.database, ref.schema);
  }
  if (ref.kind === 'folder') {
    return createNodeId('pinned-folder', connectionId, ref.database, ref.folder);
  }
  if (ref.kind === 'object') {
    return createNodeId(
      'pinned-object',
      connectionId,
      ref.database,
      OBJECT_REF_TO_OBJECT_TYPE[ref.objectType],
      ref.schema,
      ref.name,
    );
  }
  if (ref.kind === 'storage') {
    return createPinnedStorageNode({ connectionId, parentId: '', ref }).id;
  }
  // Exhaustive check — should be unreachable.
  throw new Error(`Unknown PinnedObjectRef kind: ${JSON.stringify(ref)}`);
}

/**
 * Maps a pinned wrapper's `PinnedObjectRef` to the id of the equivalent
 * CANONICAL tree-node (the same id the regular Databases drill-down builds for
 * that database / folder / object). Used by the refresh router (F9) and any
 * other code path that needs to delegate a pinned-wrapper action to the live
 * underlying node — the regular handlers already know what to do with the
 * canonical id, so this mapping lets the pinned section reuse them instead of
 * duplicating the routing logic.
 */
export function canonicalNodeIdForPin(
  connectionId: string,
  ref: PinnedObjectRef,
): string {
  if (ref.kind === 'database') {
    return createDatabaseNodeId(connectionId, ref.database);
  }
  if (ref.kind === 'schema') {
    return createSchemaContainerFolderNodeId(connectionId, ref.database, ref.schema);
  }
  if (ref.kind === 'folder') {
    switch (ref.folder) {
      case 'tables': return createTablesFolderNodeId(connectionId, ref.database);
      case 'views': return createViewsFolderNodeId(connectionId, ref.database);
      case 'storedProcedures':
        return createStoredProceduresFolderNodeId(connectionId, ref.database);
      case 'functions': return createFunctionsFolderNodeId(connectionId, ref.database);
      case 'programmability':
        return createProgrammabilityFolderNodeId(connectionId, ref.database);
      case 'packages': return createPackagesFolderNodeId(connectionId, ref.database);
      case 'sequences': return createSequencesFolderNodeId(connectionId, ref.database);
      case 'synonyms': return createSynonymsFolderNodeId(connectionId, ref.database);
      case 'dbLinks': return createDbLinksFolderNodeId(connectionId, ref.database);
      case 'materializedViews':
        return createMaterializedViewsFolderNodeId(connectionId, ref.database);
      case 'types': return createTypesFolderNodeId(connectionId, ref.database);
      case 'directories':
        return createDirectoriesFolderNodeId(connectionId, ref.database);
      case 'tableTypes':
        return createTableTypesFolderNodeId(connectionId, ref.database);
      case 'aliasTypes':
        return createAliasTypesFolderNodeId(connectionId, ref.database);
      case 'clrTypes':
        return createClrTypesFolderNodeId(connectionId, ref.database);
    }
  }
  if (ref.kind === 'object') {
    return createDatabaseObjectNodeId(
      connectionId,
      ref.database,
      OBJECT_REF_TO_OBJECT_TYPE[ref.objectType],
      ref.schema,
      ref.name,
    );
  }
  if (ref.kind === 'storage') {
    return storageNodeId(connectionId, ref.resource);
  }
  throw new Error(`Unknown PinnedObjectRef kind: ${JSON.stringify(ref)}`);
}

/**
 * Returns the CANONICAL `node.type` literal a pinned wrapper should be treated
 * as for context-menu generation, refresh routing, and similar `node.type`
 * switches. Without this mapping, pinned rows fell through to a `Copy name` +
 * `Refresh` fallback instead of getting the full set of scripting / select-top /
 * etc. actions the underlying database / folder / object row offers (F4).
 *
 * Returns `null` if the node isn't a pinned wrapper (callers should skip the
 * canonicalisation in that case and use `node.type` directly).
 */
export function canonicalNodeTypeForPin(node: { type: string; pinRef?: PinnedObjectRef }): string | null {
  if (
    node.type !== 'pinned-database' &&
    node.type !== 'pinned-schema' &&
    node.type !== 'pinned-folder' &&
    node.type !== 'pinned-object'
  ) {
    return null;
  }
  if (!node.pinRef) return null;
  const ref = node.pinRef;
  if (ref.kind === 'database') return 'database';
  if (ref.kind === 'schema') return 'schema-container-folder';
  if (ref.kind === 'folder') return FOLDER_REF_TO_NODE_TYPE[ref.folder];
  if (ref.kind === 'object') return OBJECT_REF_TO_OBJECT_TYPE[ref.objectType];
  return null;
}

/**
 * Creates a pinned-database wrapper node.
 *
 * 2026-05-26 — the user explicitly overrode the original "F1" decision that
 * pinned wrappers should be non-expandable shortcuts. Pinned-database is now
 * a full drill-in node: it expands, child folders render under it (with
 * `pinned-desc<->...` ids so they don't collide with the canonical
 * Databases-drill-down siblings), and child fetches reuse the same TanStack
 * Query cache keys as the canonical row.
 */
export function createPinnedDatabaseNode(args: {
  connectionId: string;
  parentId: string;
  ref: Extract<PinnedObjectRef, { kind: 'database' }>;
  metadata: DatabaseNodeMetadata;
}): PinnedDatabaseNode {
  return {
    id: pinnedNodeId(args.connectionId, args.ref),
    parentId: args.parentId,
    name: args.ref.database,
    type: 'pinned-database',
    // The underlying icon key in `treeNodeIconMap` — TreeNodeIcon looks up
    // `iconMap[node.icon]` before falling back to `iconMap[node.type]`, so
    // setting `icon` lets the pinned row render the same icon as the regular
    // database/folder/object row would (database icon, folder icon, etc.).
    icon: 'database',
    canHaveChildren: true,
    isExpanded: false,
    children: [],
    metadata: args.metadata,
    pinRef: args.ref,
    resolution: 'unresolved',
  };
}

/**
 * Creates a pinned-schema wrapper node. Wraps a `SchemaContainerFolderNode`
 * (the row rendered under a database when schema-grouping is active) so the
 * user can keep frequent schemas one click away.
 */
export function createPinnedSchemaNode(args: {
  connectionId: string;
  parentId: string;
  ref: Extract<PinnedObjectRef, { kind: 'schema' }>;
  metadata: SchemaContainerFolderMetadata;
}): PinnedSchemaNode {
  return {
    id: pinnedNodeId(args.connectionId, args.ref),
    parentId: args.parentId,
    name: args.ref.schema,
    type: 'pinned-schema',
    icon: 'schema-container-folder',
    canHaveChildren: true,
    isExpanded: false,
    children: [],
    metadata: args.metadata,
    pinRef: args.ref,
    resolution: 'unresolved',
  };
}

/**
 * Creates a pinned-folder wrapper node (Tables / Views / Stored Procedures /
 * Functions / Programmability).
 *
 * Expandable — on expand, the relevant object listing is fetched (via the
 * canonical TanStack Query key) and rendered with `pinned-desc<->...` ids.
 */
export function createPinnedFolderNode(args: {
  connectionId: string;
  parentId: string;
  ref: Extract<PinnedObjectRef, { kind: 'folder' }>;
  metadata: FolderNodeBaseMetadata;
}): PinnedFolderNode {
  const label: Record<typeof args.ref.folder, string> = {
    tables: 'Tables',
    views: 'Views',
    storedProcedures: 'Stored Procedures',
    functions: 'Functions',
    programmability: 'Programmability',
    packages: 'Packages',
    sequences: 'Sequences',
    synonyms: 'Synonyms',
    dbLinks: 'DB Links',
    materializedViews: 'Materialized Views',
    types: 'Types',
    directories: 'Directories',
    tableTypes: 'User-Defined Table Types',
    aliasTypes: 'User-Defined Data Types',
    clrTypes: 'User-Defined Types (CLR)',
  };
  return {
    id: pinnedNodeId(args.connectionId, args.ref),
    parentId: args.parentId,
    name: label[args.ref.folder],
    type: 'pinned-folder',
    folderKind: args.ref.folder,
    icon: FOLDER_REF_TO_NODE_TYPE[args.ref.folder],
    canHaveChildren: true,
    isExpanded: false,
    children: [],
    metadata: args.metadata,
    pinRef: args.ref,
    resolution: 'unresolved',
  };
}

/**
 * Creates a pinned-object wrapper node (table / view / stored procedure / function).
 *
 * `objectType` on the node is the kebab-case `DatabaseObjectType` literal —
 * NOT the camelCase form from the on-disk ref. Without this translation,
 * downstream switches keyed on the canonical literal silently miss pinned
 * stored-procedures / functions.
 *
 * Tables and views are expandable (revealing Columns / Keys / ... sub-folders);
 * stored procedures and functions are leaves — matching the canonical
 * `createDatabaseObjectNode` (which gates `canHaveChildren` on
 * `['table','view','package']`).
 */
export function createPinnedObjectNode(args: {
  connectionId: string;
  parentId: string;
  ref: Extract<PinnedObjectRef, { kind: 'object' }>;
  metadata: FolderNodeBaseMetadata;
}): PinnedObjectNode {
  const objectType = OBJECT_REF_TO_OBJECT_TYPE[args.ref.objectType];
  return {
    id: pinnedNodeId(args.connectionId, args.ref),
    parentId: args.parentId,
    // Schemaless engines (Kusto/ADX) pin with an empty schema — render just the
    // object name rather than a leading-dot `.StormEvents`.
    name: args.ref.schema ? `${args.ref.schema}.${args.ref.name}` : args.ref.name,
    type: 'pinned-object',
    objectType,
    icon: objectType,
    canHaveChildren: objectType === 'table' || objectType === 'view',
    isExpanded: false,
    children: [],
    metadata: { ...args.metadata, schema: args.ref.schema, name: args.ref.name },
    pinRef: args.ref,
    resolution: 'unresolved',
  };
}

/**
 * Creates a pinned-workspace wrapper node (a saved Table Explorer view, a
 * text/file query, or a notebook — see `PinnedWorkspaceRef`).
 *
 * Unlike the object-pin wrappers, a workspace pin references no live schema
 * object: it is a LEAF (never expands, `canHaveChildren: false`) that opens a
 * linked workspace tab when activated (wired in Task 17). Because it carries
 * its own definition payload it is self-contained — its `resolution` is always
 * `'resolved'` (there is no parent listing to look up in the query cache), and
 * the builder never routes it through `resolvePin`.
 *
 * The node id uses the `:` separator (`{parentId}:pinned-workspace:{ref.id}`)
 * rather than the canonical `<->` scheme: a workspace pin has no canonical
 * tree-node equivalent, no descendants, and dedups by `ref.id`. NOTE: the id
 * still FLOWS THROUGH the `<->`-based parsers at runtime — uiStore's
 * `setSelectedNodeId` runs `getConnectionIdFromNodeId` / `getDatabaseFromNodeId`
 * on every selected id, and `parseNodeId` splits this id into
 * `['connection', '{cid}:pinned-workspace:{ref.id}']`, matching none of their
 * pinned branches (mangled connection id, no database). Callers needing the
 * pin's identity must read `node.pinRef` / `node.metadata` (the explorer click
 * handler does); consumers holding only the id string must special-case the
 * `:pinned-workspace:` marker (see `createSelectionSnapshot` in mcp-context).
 */
export function createPinnedWorkspaceNode(args: {
  connectionId: string;
  parentId: string;
  ref: PinnedWorkspaceRef;
  engine: string;
}): PinnedWorkspaceNode {
  const { connectionId, parentId, ref, engine } = args;
  return {
    id: `${parentId}:pinned-workspace:${ref.id}`,
    parentId,
    name: ref.name,
    type: 'pinned-workspace',
    workspaceKind: ref.kind,
    canHaveChildren: false,
    isExpanded: false,
    children: [],
    metadata: { connectionId, database: ref.database, engine },
    pinRef: ref,
    resolution: 'resolved',
  };
}
